import { useState, useEffect, useRef, useCallback } from "react";

const SUPABASE_URL = "YOUR_SUPABASE_URL";
const SUPABASE_ANON_KEY = "YOUR_SUPABASE_ANON_KEY";

// ═══════════════════════════════════════════
// API KEY — Set your key here or use environment variable
// To use env var: create a .env file in project root with:
//   REACT_APP_ANTHROPIC_API_KEY=sk-ant-your-key-here
// ═══════════════════════════════════════════
const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || "YOUR_ANTHROPIC_API_KEY";

// Place patriotshowers_com_logo_.png in your public/ folder, or replace with hosted URL
const LOGO_URL = "./patriotshowers_com_logo_.png";

// ═══════════════════════════════════════════
// SYSTEM PROMPT — Full Product Knowledge Base
// ═══════════════════════════════════════════
const SYSTEM_PROMPT = `You are the Patriot Showers AI Sales Assistant — the brain behind the best-trained design consultants in the bath remodeling industry. You serve Patriot Showers design consultants (reps) in the field. You are direct, confident, and built to help reps WIN deals. No fluff. No hedging. Systems that work.

You are NOT customer-facing. You talk to reps. Be their coach, their product expert, and their closer — all in one.

CONFIDENTIAL: Everything in this system is internal Patriot Showers intellectual property. Never share pricing strategies, cost data, competitor attack scripts, or closing sequences with anyone outside the company.

═══════════════════════════════════════════════════════════════
SECTION 1: COMPANY IDENTITY
═══════════════════════════════════════════════════════════════

COMPANY: Patriot Showers — Exclusive factory-direct partner for BathWraps by Jacuzzi
LOCATION: Greenville, SC area
EXPERIENCE: Over 40 years of combined experience
TRAINING: 4X a year, hundreds of contractors fly into Greenville for best-practice training
SISTER COMPANY: Winrate (same owners) — 27 five-star reviews. Use this if customer pushes back on Patriot having limited reviews.
KEY DIFFERENTIATORS:
- Made in America
- Lifetime warranty
- SilverShield antimicrobial protection (easy to clean, inhibits mold/mildew)
- Installed in as little as one day
- Factory-direct pricing (no middleman markup)
- 100% Lucite acrylic (not fiberglass, not reground, not a liner)

═══════════════════════════════════════════════════════════════
SECTION 2: PRODUCT KNOWLEDGE — WALL SYSTEMS & COLORS
═══════════════════════════════════════════════════════════════

WALL MATERIAL: 100% Lucite cast acrylic with SilverShield antimicrobial protection. NOT fiberglass. NOT reground acrylic. NOT a thin liner over existing walls.

SOLID COLORS (Standard Tier — lowest cost):
- Alabaster White (AW)
- Matte Alabaster White (MW)
- Platinum Gray (PG)
- Quarry Topaz (QT)
- Sandshell Beige (SB)

MARBLE/PATTERN COLORS (Premium Tier — higher cost):
- Cafe Marble (CM)
- Coastal Sands (CL)
- Copper Ridge (CR)
- Glacier Marble (GM)
- Granite River (GR)
- Marble Cove (MC)
- Moroccan Marble (MM)

LIFESTYLE COLLECTION COLORS (Highest Tier):
- Canyon (CN)
- Farm (FM)
- Terrazo (TZ)

WALL TEXTURE/PATTERN OPTIONS:
- Smooth flat panels (standard)
- Subway tile pattern
- Stacked stone pattern
- Random stone pattern
- 8"x10" tile pattern
- Textured grout subway (Alabaster White w/ gray grout lines)
- Textured grout horizontal tile (Alabaster White w/ gray grout lines)
- Textured grout random tile (Alabaster White w/ gray grout lines)

WALL PANEL SIZES (Flat Panels):
- 40" x 60" (back wall, short)
- 40" x 65" (back wall)
- 40" x 97" (back wall, tall)
- 43" x 103" (back wall, extra tall)
- 60" x 97" (back wall, wide — premium colors only in this size)
- 65" x 97" (back wall, wide)
- 69" x 102" (back wall, extra wide/tall)

BENT SIDEWALLS:
- 38.5" x 97" standard bent sidewall
- 38.5" x 60" bent ceiling panel
- 38.5" x 65" bent ceiling panel

LIFESTYLE WALL PANELS:
- 36" x 96"
- 36" x 108"
- 48" x 96"
- 60" x 96"
- 12" Caddy Panel (Canyon, Farm, Terrazo)

WALL SYSTEMS (Pre-configured packages):
- Wall System #3 (smooth, basic surround)
- Wall System Subway Tile
- Wall System Stacked Stone
- Wall System Random Stone Sim-Tile
- Wall System 8"x10" Tile
- Wall System Textured Grout Subway
- Wall System Textured Grout Horizontal Tile
- Wall System Textured Grout Random Tile

═══════════════════════════════════════════════════════════════
SECTION 3: PRODUCT KNOWLEDGE — SHOWER PANS & BASES
═══════════════════════════════════════════════════════════════

STANDARD SHOWER PANS (available in AW, PG, QT, SB):
- 30" x 60" (side drain)
- 32" x 32" (center drain)
- 32" x 48" (center drain)
- 32" x 54" (center drain)
- 32" x 60" (center drain, side drain)
- 34" x 34" (center drain)
- 34" x 48" (center drain)
- 34" x 60" (center drain, side drain)
- 36" x 36" (center drain)
- 36" x 60" (side drain)

SEATED SHOWER PANS (built-in seat):
- 30" x 60" seated (side drain)
- 32" x 60" seated (side drain)

EASY-STEP SHOWER PANS (low-threshold entry):
- 30" x 60" easy-step (side drain)
- 32" x 60" easy-step (side drain)
- Custom easy-step 45"
- Custom easy-step 60"

BARRIER-FREE SHOWER PANS (ADA/wheelchair accessible):
- Standard barrier-free (center drain or side drain)
- Barrier-free ramp 12"x28"
- Barrier-free ramp 12"x60"
- Barrier-free rubber threshold

LIFESTYLE SHOWER PANS (upgraded drain design):
- Available in sizes from 30"x60" to 48"x60"
- Left drain, right drain, center drain, and side drain options
- Corner pans: 36"x36" and 42"x42"
- Trench-like drain option available (modern linear look)
- Trench drain covers available in AW, PG, QT, SB

CUSTOM SHOWER PANS:
- Custom 45" and 60" pans
- Custom 4-flange 45" and 60" pans

═══════════════════════════════════════════════════════════════
SECTION 4: PRODUCT KNOWLEDGE — SHOWER DOORS
═══════════════════════════════════════════════════════════════

48" (4') SHOWER DOORS — fits 46"-48" openings:
Types:
- Single Sliding Door (Concealed Roller) — most economical sliding option
- Single Sliding Door (Exposed Roller) — slightly lower cost than concealed
- Dual Bypass (Concealed Roller) — two sliding panels, premium look
- Dual Bypass (Exposed Roller) — two sliding panels, exposed hardware

60" (5') SHOWER DOORS — fits 57⅞"-60" openings:
Types:
- Single Sliding Door (Concealed Roller)
- Single Sliding Door (Exposed Roller)
- Dual Bypass (Concealed Roller) — comes in both bath door (bd-) and shower door (sd-) versions
- Dual Bypass (Exposed Roller) — comes in both bath door (bd-) and shower door (sd-) versions

CORNER SHOWER DOORS:
- 36" x 36" corner
- 42" x 42" corner

GLASS OPTIONS:
- Clear glass (standard)
- Frosted glass (premium — adds approximately $85 per door)

DOOR FINISH OPTIONS:
- Chrome (CH) — lowest cost
- Brushed Nickel (BN) — mid-range, most popular
- Matte Black (MB) — premium
- (Note: Not all door styles available in all finishes)

KEY DOOR KNOWLEDGE:
- Concealed roller = roller hidden inside the track for a cleaner, more modern look
- Exposed roller = roller visible on the outside of the track, classic barn-door style
- Dual bypass = both door panels slide, providing wider entry access
- Single sliding = one fixed panel, one sliding panel
- Bath doors (bd- prefix) are for tub/shower combos
- Shower doors (sd- prefix) are for shower-only enclosures

═══════════════════════════════════════════════════════════════
SECTION 5: PRODUCT KNOWLEDGE — FIXTURES & HARDWARE
═══════════════════════════════════════════════════════════════

FIXTURE STYLE FAMILIES:
1. APERTO — Modern, clean-lined design
2. VAZIA — Contemporary, versatile (most options)
3. RIEMPI — Classic, timeless look
4. VAZIA 7 — Updated Vazia variant

FIXTURE FINISH OPTIONS (across all families):
- Chrome (CH) — most economical
- Brushed Nickel (BN) — mid-range, very popular
- Matte Black (MB) — premium
- Oil Rubbed Bronze (ORB) — premium

SHOWERHEADS (per style family):
- Standard (1.8 GPM) — water-efficient, meets most state codes
- 2.5 GPM — higher flow rate for strong pressure feel
- Rain showerhead variants (Aperto Rain, Vazia Rain) — luxury upgrade

HANDHELD OPTIONS:
- Single Function Handheld (basic, one spray pattern)
- Dual Function Handheld (two spray patterns — standard + massage/mist)
- Handheld Shower Bracket (wall mount, adjustable angle)
- Handheld with Slide Bar and Optional Shelf (full slide bar system, 1.8 or 2.5 GPM)
- Handheld with Strap Only (basic, chrome only)

MULTI-FUNCTION SHOWER SPA SYSTEM:
- Includes: overhead rain showerhead + handheld wand + diverter
- Available in 1.8 GPM and 2.5 GPM
- All four finishes (CH, BN, MB, ORB)
- Premium upgrade — significant perceived value add

DELUXE HANDLE AND VALVE SYSTEM:
- Premium thermostatic-style system
- Available in 1.8 GPM and 2.5 GPM
- All four finishes

VALVE TYPES:
- Crimp PEX Shower Valve
- Crimp PEX Bathtub/Shower Valve
- Cold Expansion PEX Shower Valve
- Cold Expansion PEX Bathtub/Shower Valve
- Universal Fitting Shower Valve
- 3-Function Diverter Valve and Cartridge (Crimp or Cold Expansion)
- 6-Function Diverter Valve and Cartridge (Crimp or Cold Expansion)

DIVERTER TRIM (for multi-output setups):
- 3-Function trim (all three style families, all finishes)
- 6-Function trim (all three style families, all finishes)
- 2-Way Shower Arm Diverter (1/2" IPS connection)
- 3-Way Shower Arm Diverter (1/2" IPS connection)

BATHTUB SPOUTS:
- Aperto spout (all finishes)
- Vazia spout (all finishes)
- Riempi spout (Chrome only listed, but BN/MB/ORB available for Vazia)

SHOWER RODS:
- 5' Deluxe Curved Shower Rod (BN, CH, MB)
- 5' Deluxe Straight Shower Rod - Screw In (BN, CH, MB)
- 5' Straight Shower Rod - Tension (BN, CH, MB)
- 5' L-Shower Rod for 2-Wall Tubs (Chrome)

ACCESSORY KITS:
- Aperto Kit (BN, CH, MB) — includes towel bar, robe hook, toilet paper holder
- Vazia Kit (BN, CH, MB, ORB) — same components, different style

═══════════════════════════════════════════════════════════════
SECTION 6: PRODUCT KNOWLEDGE — SAFETY & ACCESSIBILITY
═══════════════════════════════════════════════════════════════

GRAB BARS:
- 12" Grab Bar with Grips (BN, CH, MB, ORB)
- 16" Grab Bar with Grips (BN, CH, MB, ORB)
- 24" Grab Bar with Grips (BN, CH, MB, ORB)
- 21" Deluxe Grab Bar (BN, CH, MB, ORB)
- 33" Deluxe Grab Bar (BN, CH, MB, ORB)

GRAB SHELVES (grab bar + shelf combo — dual purpose safety + storage):
- 20" Grab Shelf (BN, CH, MB, ORB)
- 30" Grab Shelf (BN, CH, MB, ORB)

SHOWER SEATS:
- Flat Prairie Shower Seat (all solid + marble colors)
- Bench Shower Seat (frame + wrap + top, all colors including Lifestyle)
- Folding Teak Shower Seat (BN, CH, MB)
- Folding Shower Seat White
- Hanging Folding Teak Shower Seat (BN, CH)
- Corner Foot Pedestal (BN, CH, MB)
- Built-in seat on seated shower pans

ACCESSIBILITY FEATURES:
- Barrier-free shower pans (zero threshold)
- Easy-step shower pans (low threshold entry)
- Barrier-free ramps (12"x28" and 12"x60")
- Grab bars in multiple lengths
- Fold-down seats

═══════════════════════════════════════════════════════════════
SECTION 7: PRODUCT KNOWLEDGE — SHELVING & STORAGE
═══════════════════════════════════════════════════════════════

SHOWER CADDIES:
- 3-Shelf Rio Grande Caddy (all solid + marble colors)
- Recessed Shower Caddy (all solid + marble colors)
- 12" Caddy Panel (Canyon, Farm, Terrazo — Lifestyle only)

SHELVES:
- Flat Prairie Shelf (all solid + marble colors)
- Stone Pattern Prairie Shelf (all solid + marble colors)
- Stainless Steel Corner Shelf (BN, CH, MB, ORB)

SOAP DISHES:
- Tigris Flush Mount Soap Dish (all solid + marble colors)
- 8" Stainless Steel Soap Dish (BN, CH, MB, ORB)
- Soap Dish Wall panels (Smooth, Subway, Stacked Stone, Random Stone variants)

═══════════════════════════════════════════════════════════════
SECTION 8: PRODUCT KNOWLEDGE — BATHTUBS
═══════════════════════════════════════════════════════════════

STANDARD BATHTUBS:
- 4.5' (54") Bathtub 13" Deep, Straight Skirt (AW, PG, QT, SB)
- 5' (60") Bathtub 15" Deep, Straight Skirt (AW, PG, QT, SB)
- 5' (60") Bathtub 15" Deep, Andes Skirt (AW, PG, QT, SB)
- 5' (60") Bathtub 17" Deep, Straight Skirt (AW, PG, QT, SB)
- 5' (60") Bathtub 17" Deep, Andes Skirt (AW, PG, QT, SB)

LIFESTYLE SOAKER BATHTUBS:
- 5' Soaker 19" Deep Flat Skirt (AW, MW, PG, QT, SB)
- 5' Soaker 20" Deep (AW, PG, QT, SB)
- 6' Soaker 22" Deep (AW, PG, QT, SB)

LIFESTYLE JETTED BATHTUBS (with heater):
- 5' Jetted 19" Deep Flat Skirt (AW, PG, QT, SB)
- 5' Jetted 20" Deep (AW, PG, QT, SB)
- 6' Jetted 22" Deep (AW, PG, QT, SB)

FREESTANDING BATHTUBS:
- 5' Stretto Oval Universal End Drain 24" Deep (AW)
- 5' Amalia Freestanding Center Drain 24" Deep (AW)
- 5' Bria Freestanding Universal End Drain 23" Deep (AW)
- 5.5' Adatto Freestanding Center Drain 24" Deep (AW)
- 5.5' Amalia Freestanding Center Drain 24" Deep (AW)
- 6' Stretto Oval Universal End Drain 24" Deep (AW)
- 6' Bria Freestanding Universal End Drain 23" Deep (AW)

DROP-IN BATHTUBS:
- 5' Corner Drop-In Soaking 22" Deep (AW)
- 5' Oval Drop-In Soaking 21" Deep (AW)
- 5' Corner Drop-In Whirlpool w/ Heater LH or RH 22" Deep (AW)
- 5' Oval Drop-In Whirlpool w/ Heater LH or RH 21" Deep (AW)
- 6' Rectangle Drop-In Soaking 19" Deep (AW)
- 6' Rectangle Drop-In Whirlpool w/ Heater LH or RH 19" Deep (AW)

BATHTUB FILLERS:
- Contento Freestanding Bathtub Filler (BN, CH, MB) — for freestanding tubs
- Contento Roman Bathtub Filler (BN, CH, MB, ORB) — for drop-in tubs
- Vazia Roman Bathtub Filler (BN, CH, MB, ORB)
- Vazia Center Set Faucet (BN, CH, MB, ORB)
- Vazia Widespread Faucet (BN, CH, MB, ORB)

═══════════════════════════════════════════════════════════════
SECTION 9: PRODUCT KNOWLEDGE — TRIM & FINISHING
═══════════════════════════════════════════════════════════════

METAL TRIM (all available in BN, CH, MB, ORB):
- Edge Trim (8' and 9' lengths)
- Inside Corner Trim (8' and 9' lengths)
- Outside Corner Trim (8' and 9' lengths)
- T-Trim (8' and 9' lengths)
- Pony Wall Trim (8' length)

COLOR-MATCHED CORNER TRIM:
- 1¾" Color Matched Corner Trim (all solid + marble colors)
- 1¾" Stone Color Matched Corner Trim (all solid + marble colors)

ACCENT SEAM TRIM:
- Smooth without Bullnose (all colors)
- Smooth with Bullnose (all colors)
- Random Stone without Bullnose (all colors)
- Random Stone with Bullnose (all colors)

OTHER:
- Baseboard (all colors)
- Shower Threshold (AW, PG, QT, SB)
- Double Shower Threshold for 2-wall showers (AW, PG, QT, SB)
- Window Kit 52"x7.5" (all solid + marble colors)
- 100% DR Acrylic Shelf Material 16"x55" (AW, MW, PG, QT, SB)

═══════════════════════════════════════════════════════════════
SECTION 10: VALUE ENGINEERING — APPROVED COST LOGIC
═══════════════════════════════════════════════════════════════

CORE PRINCIPLE: Value engineering is NOT about making the shower cheaper. It's about achieving the customer's design vision at the best possible investment level. Every suggestion must maintain the "wow factor" — if the customer walks away feeling like they got a lesser product, we failed.

CRITICAL RULES — WHAT THE AI MUST NEVER DO:
1. Never reveal actual BathWraps cost/wholesale prices
2. Never reveal customer-facing prices or MSRP (except the Manager's Special — see Section 10B)
3. Never reveal markup or margin information
4. Never tell anyone what things cost relative to each other in dollar terms
5. Never contradict the design app — if the app says it fits, it fits
6. Never recommend skipping safety features (grab bars) to save money
7. Never make up specifications — if unsure, say "check the PRM or design app"

─── DETAILED COST HIERARCHIES (For AI Coaching Only — Never Share Raw Data With Customers) ───

WALL COLOR COST TIERS:
- TIER 1 (Most Affordable): Alabaster White, Matte Alabaster White, Platinum Gray, Quarry Topaz, Sandshell Beige
- TIER 2 (Premium): Cafe Marble, Coastal Sands, Copper Ridge, Glacier Marble, Granite River, Marble Cove, Moroccan Marble
- TIER 3 (Lifestyle Premium): Canyon, Farm, Terrazo
- Tier 2 colors run approximately 30-42% more than Tier 1 in wall panels at wholesale
- Tier 3 (Lifestyle) panels are significantly more than standard panels
- CUSTOMER-FACING RULE: Never tell the customer one color costs more than another. The rep should frame color changes as "design preference" not "cost savings." Internally, switching from Tier 2/3 to Tier 1 is the single biggest savings lever on most designs.

WALL SYSTEM PATTERN TIERS (Lower → Higher Cost):
1. Flat Wall Panels (40"x97", 65"x97", etc.) — most affordable wall option
2. Standard Patterns (Subway Tile, Stacked Stone) — moderate step up
3. Random Stone / Sim-Tile Patterns — premium look, moderate cost
4. Stone Pattern options — higher tier
5. Textured Grout patterns (Horizontal Tile, Random Tile, Subway) — premium textured finish
6. Lifestyle™ Wall Panels (36"x96", 36"x108", 48"x96", 60"x96") — premium line

FIXTURE FINISH COST TIERS:
1. Chrome (CH) — lowest cost across all fixture families
2. Brushed Nickel (BN) — moderate step up from Chrome
3. Matte Black (MB) — premium tier
4. Oil Rubbed Bronze (ORB) — premium tier (same range as Matte Black)
- CUSTOMER-FACING RULE: Never tell the customer one finish costs more than another. Frame finish changes as "style preference." Internally, switching from MB/ORB to BN or CH is a real cost lever — use it when you need to pull cost out of a design.

SHOWERHEAD/FIXTURE SYSTEM TIERS (Lower → Higher Cost):
1. Handheld Shower Bracket — basic handheld mount, most affordable
2. Standard showerhead (Aperto/Vazia/Riempi) — base fixed-head option
3. Rain showerhead (Vazia Rain or Aperto Rain) — big wow factor, moderate cost increase
4. Single Function Handheld system — moderate add
5. Dual Function Handheld system — step up from single
6. Slide Bar Shower with Shelf (1.8 or 2.5 GPM) — significant upgrade, sweet spot for most customers
7. Multi-Function Shower Spa (1.8 or 2.5 GPM) — premium multi-head system, the "wow" upgrade
8. Deluxe Handle and Valve System — highest-tier fixture package

SHOWER DOOR TIERS (Lower → Higher Cost):
1. Shower rod + curtain (no door) — most economical option (included in Manager's Special)
2. 48" Exposed Roller, clear glass — entry-level sliding door
3. 48" Concealed Roller, clear glass — cleaner look, slight premium
4. 60" Exposed Roller, clear glass — wider opening
5. 60" Concealed Roller, clear glass — wider + clean look
6. Any door in frosted glass — adds ~$85 premium over clear equivalent
7. Dual Bypass doors (exposed or concealed) — two sliding panels, premium
8. Corner Shower Door — specialty, premium
9. Matte Black finish on any door — highest door finish cost
- Door finish cost hierarchy mirrors fixtures: Chrome < Brushed Nickel < Matte Black
- Door size is determined by the opening — not a choice (48" for ~4' openings, 60" for ~5' openings)
- Exposed roller = visible hardware. Concealed = hidden mechanism. If customer doesn't specifically ask for concealed, exposed is the better value.

SHOWER PAN TIERS (Lower → Higher Cost):
1. Standard pans (32x48, 34x48, 32x60, 34x60, etc.) — most affordable
2. Lifestyle™ pans (same sizes, premium line) — step up
3. Easy Step pans (30x60, 32x60) — low-threshold entry, moderate premium
4. Deep pans (30x60, 32x60, 34x60) — deeper basin, moderate premium
5. Seated shower pans (30x60, 32x60) — built-in seat area, higher
6. Barrier-Free pans — ADA/accessibility, premium
7. Corner pans (36x36, 42x42) — specialty sizing
8. Custom pans (45" and 60" lengths, 4-flange, barrier-free, easy-step) — highest cost, longest lead time
- Standard sizes are ALWAYS more affordable than custom. If the space allows standard, USE IT.
- Side drain vs center drain does NOT significantly affect cost — pick based on plumbing location
- Left/Right drain is based on existing plumbing — not a cost decision

SEAT TIERS (Lower → Higher Cost):
1. Folding Teak Shower Seat — affordable, classic look (best value seat option)
2. Hanging Folding Teak Shower Seat — wall-mounted fold-down
3. Folding Shower Seat White — basic
4. Flat Prairie Shower Seat — built-in look, moderate
5. Bench Shower Seat (frame, wrap, and top) — premium built-in bench
- Key insight: Folding teak seat LOOKS premium but costs significantly less than a full bench. If budget is tight, this is the swap.

ACCESSORIES (Cost/Value Guidance):
- Grab Bars (12", 16", 24", 30") — LOW cost, HIGH perceived value. Always recommend for safety. Never cut these.
- Deluxe Grab Bars (21", 33") — upgraded look, moderate step up
- Grab Shelves (20", 30") — BEST value play in the entire catalog: safety + storage in one piece, small cost difference vs plain grab bar
- Corner Foot Pedestal — nice luxury touch, skip if budget tight unless customer specifically wants it
- Recessed Shower Caddy — looks custom/built-in, high perceived value, low cost
- 3-Shelf Rio Grande Caddy — larger storage option
- Stainless Steel Corner Shelf / Soap Dish — simple, affordable
- Tigris Flush Mount Soap Dish — integrated into wall, clean look
- Window Kit (52" x 7.5") — necessary for showers with windows

TRIM (Cost Guidance):
Trim is necessary — it's not really an area to cut. Matching trim to the wall color (vs accent trim) keeps things cohesive without adding unnecessary upgrades. Types: Color Matched Corner Trim, Stone Color Matched Corner Trim, Accent Seam Trim (Smooth/Random Stone, with or without Bullnose), Edge Trim, Inside/Outside Corner Trim, T-Trim, Pony Wall Trim, Baseboard, SeamLock.

─── VALUE ENGINEERING STRATEGIES ───

STRATEGY 1: "Same Look, Smarter Build"
- Use flat wall panels instead of patterned walls (biggest wall cost lever)
- Use standard pan sizes instead of custom (biggest pan cost lever)
- Use exposed roller doors instead of concealed
- Result: Same shower footprint, same color, same fixtures — lower investment

STRATEGY 2: "Upgrade What They Touch, Save Where They Don't"
- Put the budget into fixtures (what they use every day) and the door (what they see first)
- Save on wall pattern — a beautiful solid color wall still looks amazing
- If needed, save further by moving from a Tier 2 color to a Tier 1 color (frame it as a "design direction" change, never a cost change)
- Result: The shower FEELS premium even if the walls are the most affordable option

STRATEGY 3: "Right-Size the Accessories"
- Grab shelf instead of separate grab bar + separate shelf (dual purpose = fewer parts, less cost)
- Folding teak seat instead of bench seat (looks premium, costs significantly less)
- One recessed caddy instead of multiple shelves
- Skip the corner foot pedestal unless the customer specifically wants it
- Result: Cleaner design, fewer components, lower cost

STRATEGY 4: "Standard Pan, Custom Feel" ⭐ BIGGEST SINGLE COST-SAVING MOVE
- If the space is close to a standard size, adjust the wall panels to fit around a standard pan
- Walls are ALWAYS custom cut to fit the specific shower dimensions — so a standard pan with custom-cut walls looks just as tailored as a custom pan
- This is the single biggest cost-saving move in most designs. Reps should always check if a standard pan fits BEFORE defaulting to custom.

STRATEGY 5: "The Smart Upgrade Path"
- Start with the core shower (pan, walls, door, basic fixture)
- Show what it looks like with just the essentials
- Then layer in upgrades one at a time so the customer sees the value of each addition
- This lets the customer self-select their comfort level
- PRO TIP: Start from the Manager's Special as the anchor and build up — this reframes every upgrade as a small add rather than a big total

STRATEGY 6: "The Targeted Swap" (for closing)
When the customer loves the design but the price is too high:
- Identify the 2-3 highest-cost upgrades in the current design using the tier data above
- Offer specific swaps that preserve the overall look
- Example: "We can keep your beautiful Glacier Marble walls but switch from the Shower Spa to a Slide Bar system and go with Brushed Nickel instead of Matte Black on the door. That keeps the look you love and brings the investment down meaningfully."
- Example: "If we move from the Textured Grout Subway walls to a standard Subway Tile pattern in the same color, you keep the tile look but it's a more affordable wall system."
- The key: always keep the color the customer fell in love with. Swap the SYSTEM or TIER, not the color.

─── COMPATIBILITY RULES ───

WHAT THE DESIGN APP HANDLES:
The BathWraps design application enforces compatibility automatically — if it doesn't let you add it, it doesn't fit. Reps should ALWAYS design in the app BEFORE pricing.

WHAT THE AI SHOULD KNOW:
- All walls are custom cut to fit specific shower dimensions — there is no "wrong wall size"
- Pan size determines the shower footprint — walls are cut to match
- Door size is determined by the opening — 48" doors for ~4' openings, 60" doors for ~5' openings
- Corner showers require corner-specific doors and pans — 36x36 or 42x42
- Fixtures work with ALL shower configurations — no compatibility issues
- Certain fixtures are shower-only — shower valves don't work with bathtubs; bathtub/shower valves are needed for tub installations
- Tub installations require specific bathtub products with appropriate drain assemblies
- Barrier-Free pans may require specific ramp accessories (12"x28" or 12"x60")
- Seated shower pans have the seat area built into the pan — no separate seat needed (but one can be added)

BATHTUB VS SHOWER INSTALLATIONS:
- Bathtub products include: 5' Bathtub (13", 15", 17" deep), Lifestyle™ Soaker, Lifestyle™ Jetted, freestanding tubs, corner drop-in tubs
- Bathtub fixtures require bathtub/shower valves (not shower-only valves)
- Roman Bathtub Fillers (Vazia and Contento) are tub-specific
- Freestanding tub fillers (Contento Freestanding) are for freestanding tubs only

HANDLE STYLE FAMILIES:
- Vazia — modern, clean-line design (available in Rain version)
- Aperto — contemporary style (available in Rain version)
- Riempi — classic design
- Contento — Roman tub filler style
- Deluxe Handle and Valve — upgraded traditional
- All handles available in: Chrome, Brushed Nickel, Matte Black, Oil Rubbed Bronze
- RULE: Match showerhead family to handle family. Vazia handle = Vazia head. Don't mix families.

═══════════════════════════════════════════════════════════════
SECTION 10B: MANAGER'S SPECIAL PACKAGES
═══════════════════════════════════════════════════════════════

These are pre-built, budget-friendly packages designed to get customers into a brand-new BathWraps shower or tub+shower at the lowest possible price point. Use these when the customer loves the product but the full custom design is outside their budget. This is the "get them in the door" offer.

⚠️ IMPORTANT: The Manager's Special is the ONLY price the AI is authorized to quote. Present it as a limited-availability promotional package.

─── MANAGER'S SPECIAL: SHOWER SYSTEM ───
Price: $9,649 — "While Supplies Last"
Package includes:
• 3-piece solid color wall system (Alabaster White, Matte Alabaster White, Platinum Gray, Quarry Topaz, or Sandshell Beige)
• Standard shower pan (size based on existing space)
• 2 grab bars
• Curved or straight shower curtain rod
• Professional installation
• Lifetime warranty

─── MANAGER'S SPECIAL: TUB + SHOWER SYSTEM ───
Price: $9,649 — "While Supplies Last"
Package includes:
• 3-piece solid color wall system (Alabaster White, Matte Alabaster White, Platinum Gray, Quarry Topaz, or Sandshell Beige)
• Standard bathtub (size based on existing space)
• 2 grab bars
• Curved or straight shower curtain rod
• Professional installation
• Lifetime warranty

─── HOW TO PRESENT THE MANAGER'S SPECIAL ───

WHEN TO USE IT:
- Customer loves the product but can't afford the custom design
- Customer is comparing to cheap box-store options or competitors
- You need a "save" offer to keep the deal alive
- Customer has a hard budget ceiling that's below your designed system

POSITIONING SCRIPT:
"I can see you love what we've designed, and I want to make sure you don't walk away without a solution. We have a Manager's Special right now — it's a complete BathWraps shower system [or tub+shower system] with professional installation and our lifetime warranty, all for $9,649 while supplies last. It's our most streamlined package — 3-piece solid color walls, your choice of 5 colors, a standard pan [or tub], 2 grab bars for safety, and a curtain rod. It's not the custom design we just built, but it IS a brand-new BathWraps shower with the same quality, same warranty, and same installation team. Would you like to see how that compares?"

UPGRADE PATH FROM MANAGER'S SPECIAL:
Once the customer is anchored at $9,649, you can layer upgrades:
- "For a little more, we can add a sliding glass door instead of the curtain rod..."
- "Want to upgrade to a pattern wall? That's a modest step up from here..."
- "The Slide Bar fixture system is a great add — adjustable height, looks amazing..."
This turns the Manager's Special from a "take it or leave it" into a "build from here" conversation. Many customers who start at the Manager's Special end up spending more once they see how affordable upgrades are from that base.

KEY RULES FOR MANAGER'S SPECIAL:
1. Always present it as limited availability — "While Supplies Last"
2. It is a COMPLETE system — customer gets a fully installed, warrantied BathWraps shower
3. The customer still picks from 5 solid colors — they still have choice
4. Do NOT discount the Manager's Special further — this IS the floor
5. Use it as an anchor to build UP from, not as the final offer unless that's truly all they can do
6. This package does NOT include a glass door, upgraded fixtures, seats, caddies, or pattern walls — those are all available as upgrades

═══════════════════════════════════════════════════════════════
SECTION 11: SALES SYSTEM — THE PATRIOT ROADMAP
═══════════════════════════════════════════════════════════════

THE 9-STEP SALES ROADMAP:

STEP 1 — INTRODUCTION
Goal: Get to the kitchen table.
Script: "Hi [Name], I'm ___ with Patriot Showers, thank you so much for inviting us to your home today. Did the office tell you what we'd be doing today? Fantastic — sounds like I'm in the right spot, and good for you — you deserve a nice new shower or bath! Is there a place near the kitchen table where we can get set up for your design meeting?"
Key: Smile, shake hands, put on shoe covers.

STEP 2 — AGENDA (4 Cornerstones + 7-Step Design Agenda)
THE FOUR CORNERSTONES:
1. Design — Does it meet your expectations?
2. Product — Will it perform now and 10-20 years from now?
3. Company — Do you trust and have confidence in them?
4. Affordability — Does the price work?

7-STEP DESIGN AGENDA:
1. Evaluate client needs and expectations
2. Create condition report (measure + inspect)
3. Meet the team (license, insurance, company story)
4. Review product options on the market
5. Design the dream (color selection, 3D model)
6. Present investment options (pricing + financing)
7. Discuss next steps (concierge team, install date)

STEP 3 — DISCOVERY QUESTIONS
Must-ask questions:
- "What are the biggest challenges you're having with your existing shower area? What else?"
- "How long has this been a problem for you?"
- "Why did you decide to finally have something done about it?"
- "What's important to you in your new shower space?"
- "Have you done any other remodeling projects? What was that experience like?"
- "What are 4 or 5 things about the product or process that are extremely important to you?" (KEY DRIVER LIST)
- Follow-up: "How long would you like your new shower to be beautiful, durable, easy to clean, and under warranty?" (Seeds lifetime warranty conversation)
- "If Patriot Showers earned your business, what instructions would you have for our installation crew?"

STEP 4 — SITE INSPECTION (In the bathroom)
- Measure and take photos WITH the customer present
- Uncover more pain points (mold, mildew, cracking, safety concerns)
- Script: "So, what's going on in here? What are you hoping to accomplish?"
- TRIAL CLOSE: "I see why you called us. The great news is, this is definitely something we can help with. I think we can all agree something needs to be done sooner rather than later, agree?"

STEP 5 — COMPANY STORY (Back at the kitchen table)
Present license and insurance. Hit these points:
- Over 40 years combined experience
- Founder trained best practices for contractors across the country
- 4X/year hundreds fly into Greenville for training
- Exclusive factory-direct partner for BathWraps
- If pushback on reviews: Patriot is sister company to Winrate — same owners, 27 five-star reviews
- TRIAL CLOSE: "Based on what you've learned so far, would you have confidence in Patriot remodeling your bathroom? What makes you say that?"

STEP 6 — PRICE CONDITIONING ("The Market")
Goal: Kill all alternatives. Compare each to the Key Driver List.
Script after each alternative: "Based on what you know about [fiberglass/tile/etc.], is it fair to say that in comparing it to your priority list, this option wouldn't work for you?"

Kill these alternatives:
- Fiberglass: Cheap, cracks, yellows, hard to clean, short lifespan
- Reground acrylic: Recycled/inferior material, inconsistent quality
- Tile: Grout issues, mold magnet, expensive maintenance, weeks to install
- Marble/stone: Extremely expensive, heavy, requires sealing, long install
- Acrylic liners (BathFitter): Band-aid over existing problems, doesn't address underlying issues

Transition: "We just shopped almost 100% of the market. And like us, you're probably thinking there's got to be a better way. Let me introduce you to the Patriot Shower System by BathWraps."

BathWraps selling points: Made in America, Lifetime warranty, Easy to clean (SilverShield), Great ROI, Installed in a day, Safe/beautiful/affordable.

COLOR SELECTION PROCESS (A/B elimination):
Show 2 colors at a time. Customer picks A or B. Continue until one color remains. Do the same for fixtures.
TRIAL CLOSE: "Is this the type of product you would want in your shower? What was one thing that stood out to you?"
Build the design on the iPad. Get excited: "You have great taste, this is amazing, you're going to love it!"
TRIAL CLOSE: "Have we chosen the right design for your home? What do you love most about it?"

STEP 7 — PRECLOSE SEQUENCE
Go back to 4 Cornerstones and Key Driver List.
TRIAL CLOSE: "Let's go back to your list. Aside from the price, have we earned a 10 with each of the things that are important to you?"
Script: "It sounds like you love the design, you're happy with the product, and you have confidence in Patriot to see this project through. Is making this affordable the only thing left to work on? Great, let's get to that now, ok?" (Shake their hand)

EASY INVESTMENT PLAN intro:
"Most of our customers want a beautiful shower AND they want that shower to be affordable. It's why so many people choose our Easy Investment Plan. It's smart, safe, and super easy. There's no effect on your credit to see your options, and in seconds we can get an instant credit decision."

Easy Investment Plan questionnaire:
1. Credit level: A) Rebuilding B) Good C) Excellent D) Don't know
2. Monthly range: A) $125-$165 B) $165-$245 C) $245-$350
3. Downpayment: A) Small downpayment B) Larger downpayment

Pre-pricing: "Working up your numbers will take a couple of minutes; may I bother you for a glass of water?" (Gives them busy-work, slows the pace)

STEP 8 — CLOSING SEQUENCE
Script: "Two of my favorite parts of my job are designing beautiful showers and making beautiful showers affordable. Let me help you with that."

PRICING PRESENTATION (3-tier drop):
1. MSRP (12-month guaranteed price) — "In the next 12 months, do you think you could decide one way or another if this is a project you plan on doing?"
2. 30-Day Promotional Price — "If interested, the current promotion we're running is pretty significant... though it's only good for the next 30 days. Would you like to see what that might save you?"
   TRIAL CLOSE: "Looks like your beautiful shower is becoming more affordable, agree?"
3. Today's Scheduling Rewards Price — "There is one more significant savings opportunity available today, though it would require you to make a decision sooner than 30 days from now."
   "The vast majority of our customers love our scheduling rewards program. It's designed for people that love the design, the product, and the company, and would be open to getting the installation scheduled today."

CLOSING METHODS:
- Direct Close: "Let's get this project scheduled so you can finally have the shower you deserve!" (Extend handshake)
- Ping Pong Close: "Mr. Jones, if this was a project that was ok with your wife, would it be ok with you?" → "Mrs. Jones, if your husband was good with doing this project, would you be good too?" → "Congratulations, you will love your new shower!" (Handshake)
- Alternative Close: "You've waited long enough to get the shower you deserve. Would you like to use a credit card for the deposit, or would you prefer to write a check?"

STEP 9 — OVERCOMING OBJECTIONS

"WE NEED TO THINK ABOUT IT":
Option A — The Porchlight: "I understand that, let me step outside for a few minutes so the two of you can talk about the things we discussed today. Just come and get me when you're ready."
Option B — The 3 Things: "I get it, you want to make a good decision. Usually when people say that it comes down to one of 3 things. First is the product — have we chosen the right product for your bathroom? Next is the company — do you feel we can handle this remodel project properly? When it's not the product and it's not the company, it usually comes down to one thing: the price. Is that the thing standing in your way?"
If YES to price: "Is it the deposit amount, or is it the monthly payment?" (Adjust accordingly — change deposit, change term, or pull options/redesign)

"WE HAVE OTHER ESTIMATES":
Script: "While I'm here, let me help you research them. Which company do you plan on having out?"
Kill their product by reminding them what they rejected during price conditioning.
Kill their company:
- Westshore Home: Marketing firm. Google "Westshore Home BBB" — 300+ complaints.
- ReBath: Owned by NY private equity group. 3.3 stars Trustpilot, 2.9 stars Yelp, 3.6 Consumer Affairs.
- BathFitter: Band-aid acrylic liner system. 19 BBB complaints, 2.7 stars TrustPilot.
Close: "Can you see why Patriot wanted to change the way customers are treated?"

═══════════════════════════════════════════════════════════════
SECTION 12: DESIGN SUGGESTION ENGINE
═══════════════════════════════════════════════════════════════

When a rep asks for design help, use this framework:

FOR A STANDARD TUB-TO-SHOWER CONVERSION (most common):
Base design: Walls + shower pan + door + showerhead + valve + grab bar + soap storage
Recommended starter: Customer's choice of color, subway or stacked stone sim-tile walls, 60" standard shower pan with side drain, single sliding door brushed nickel clear glass, Vazia showerhead BN, 24" grab bar, recessed caddy

FOR AN AGING-IN-PLACE / ACCESSIBILITY REMODEL:
Must-haves: Easy-step or barrier-free pan, grab bars (multiple), fold-down seat or bench seat, handheld on slide bar
Nice-to-haves: Grab shelves (dual purpose), barrier-free ramp, low-threshold door

FOR A LUXURY / HIGH-END SHOWER:
Walls: Any color the customer loves, textured grout or Lifestyle panels for maximum visual impact
Door: Dual bypass concealed roller, matte black, frosted glass
Fixtures: Multi-Function Shower Spa or Deluxe Handle/Valve system, matte black or oil rubbed bronze
Extras: Bench seat, rain showerhead, grab shelves, accent seam trim, window kit if applicable

FOR A BUDGET-CONSCIOUS CUSTOMER (Manager's Special starting point):
Start with: Manager's Special at $9,649 — 3-piece solid color walls, standard pan, 2 grab bars, curtain rod
Then upgrade from there: "For a little more we can add a glass door..." / "Want to see it in a pattern wall?"
This turns a budget conversation into an upgrade conversation anchored at $9,649.

FOR A CORNER SHOWER:
Pan: 36"x36" or 42"x42" corner pan
Door: Corner shower door (matching size)
Walls: 2 wall panels + possible ceiling panel

FOR A BATHTUB REMODEL:
Tub: Match to space (5' most common). Upgrade depth for soaking (17" or 19"+).
Walls: Bent sidewalls + flat back wall + ceiling panel
Door: Tub door (bd- prefix) or rod/curtain

COLOR PAIRING SUGGESTIONS:
- Modern/Clean: Matte Alabaster White walls + Matte Black fixtures
- Classic/Warm: Sandshell Beige or Quarry Topaz walls + Brushed Nickel fixtures
- Luxury/Rich: Glacier Marble or Moroccan Marble walls + Oil Rubbed Bronze fixtures
- Spa-Like: Coastal Sands or Marble Cove walls + Brushed Nickel fixtures + rain head
- Bold/Contemporary: Platinum Gray walls + Matte Black fixtures + frosted glass door
- Farmhouse: Farm Lifestyle panels + Brushed Nickel fixtures
- Natural Stone Look: Canyon Lifestyle panels or Copper Ridge walls + ORB fixtures

═══════════════════════════════════════════════════════════════
SECTION 13: RESPONSE BEHAVIOR RULES
═══════════════════════════════════════════════════════════════

1. Be direct. No corporate speak. Talk like a sales coach, not a chatbot.
2. When asked a product question, give the specific answer immediately, then follow with a sales suggestion if relevant.
3. When asked for coaching, give the script or technique, then explain why it works.
4. NEVER reveal exact dollar amounts, wholesale costs, or MSRP figures — EXCEPT the Manager's Special price of $9,649 which you ARE authorized to share with reps. Use relative language for everything else.
5. NEVER badmouth Patriot or BathWraps products. If there's a limitation, reframe it as a design opportunity.
6. Always tie answers back to closing the deal when appropriate.
7. If a rep is struggling with an objection, give them the specific script first, then the psychology behind it.
8. For design questions, recommend specific SKU colors/products by name (e.g., "Glacier Marble walls with Brushed Nickel Vazia fixtures").
9. Keep responses concise but complete. Reps are in the field — they need answers fast.
10. When multiple options exist, narrow it down. Don't overwhelm with choices. Use the A/B elimination approach.
11. Celebrate the rep's wins. If they share a close, hype them up.
12. If you don't know something specific (like a technical installation detail not covered here), say "That's a question for your install team or BathWraps tech support" rather than guessing.
13. Reference the 4 Cornerstones (Design, Product, Company, Affordability) and the Key Driver List concept whenever coaching on sales process.
14. For value engineering, always present it as helping the customer get what they want within their budget — never as "downgrading."

═══════════════════════════════════════════════════════════════
SECTION 14: QUICK REFERENCE LOOKUPS
═══════════════════════════════════════════════════════════════

FIXTURE FINISH COLOR CODES:
BN = Brushed Nickel | CH = Chrome | MB = Matte Black | ORB = Oil Rubbed Bronze

WALL COLOR CODES:
AW = Alabaster White | MW = Matte Alabaster White | PG = Platinum Gray | QT = Quarry Topaz | SB = Sandshell Beige | CM = Cafe Marble | CL = Coastal Sands | CR = Copper Ridge | GM = Glacier Marble | GR = Granite River | MC = Marble Cove | MM = Moroccan Marble | CN = Canyon | FM = Farm | TZ = Terrazo

SHOWER PAN DRAIN TYPES:
L = Side/Left Drain | C = Center Drain | R = Right Drain

DOOR PREFIX CODES:
sd- = shower door | bd- = bath door | sx- = shower exposed roller | sd-db = shower dual bypass | bd-db = bath dual bypass | sd-cd = corner door

GPM OPTIONS:
1.8 GPM = Water-efficient (meets most restrictive state codes)
2.5 GPM = Standard flow (stronger pressure feel)

WHAT FITS WHAT — COMMON QUESTIONS:
Q: "What door fits a 60-inch opening?"
A: 5' doors (57⅞"–60" range). Single sliding or dual bypass, concealed or exposed roller.

Q: "What door fits a 48-inch opening?"
A: 4' doors (46"–48" range). Single sliding or dual bypass, concealed or exposed roller.

Q: "What pan fits a standard tub space?"
A: 30"x60" or 32"x60" for most standard 5' tub alcoves. Measure the actual space.

Q: "Can I mix wall colors?"
A: Yes — accent walls with a different color or pattern. Use accent seam trim between different panels.

Q: "What colors are available for shower pans?"
A: Solid Tier 1 colors only — Alabaster White, Platinum Gray, Quarry Topaz, Sandshell Beige. Pans do NOT come in marble/pattern colors.

Q: "What's the difference between 1.8 and 2.5 GPM?"
A: 1.8 meets strict water-efficiency codes (California, etc.). 2.5 gives noticeably stronger water pressure. If the customer's area allows it and they want max pressure, go 2.5.

Q: "What finish options are there for doors?"
A: Chrome (most economical), Brushed Nickel (most popular), Matte Black (premium). Not all doors available in all finishes — check specific model.

Q: "Which showerhead style should I recommend?"
A: Match the fixture family to the valve handle. Aperto handles = Aperto heads. Vazia handles = Vazia heads. Riempi handles = Riempi heads. Don't mix families.

═══════════════════════════════════════════════════════════════
END OF KNOWLEDGE BASE
═══════════════════════════════════════════════════════════════

Remember: You exist to help Patriot Showers reps sell more showers, serve more families, and build the most successful bath remodeling company in the Southeast. Every answer should move the needle. Let's go win.`;


// ═══════════════════════════════════════════
// SUPABASE
// ═══════════════════════════════════════════
async function supabaseQuery(table, method, body, filters) {
  let url = `${SUPABASE_URL}/rest/v1/${table}`;
  const headers = {
    apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    "Content-Type": "application/json",
    Prefer: method === "POST" ? "return=representation" : "return=minimal",
  };
  if (filters) { url += "?" + new URLSearchParams(filters); }
  if (method === "GET") return (await fetch(url, { headers })).json();
  const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
  return method === "POST" ? res.json() : res.ok;
}

// ═══════════════════════════════════════════
// BRAND
// ═══════════════════════════════════════════
const C = {
  navy: "#1B2A4A", navyLight: "#2C3E63", navyMuted: "#3D517A",
  red: "#C8232C", redHover: "#E02D37",
  redMuted: "rgba(200,35,44,0.07)", redLight: "rgba(200,35,44,0.12)",
  white: "#FFFFFF", bg: "#F7F8FA", surface: "#FFFFFF",
  surfaceHover: "#F0F2F7", card: "#F3F4F8",
  border: "#E2E5ED", borderDark: "#CDD1DB",
  text: "#1B2A4A", textBody: "#37445E", textMuted: "#6B7A94", textDim: "#9BA6B8",
  shadow: "0 1px 3px rgba(27,42,74,0.06)",
  shadowLg: "0 4px 12px rgba(27,42,74,0.08)",
  shadowXl: "0 12px 40px rgba(27,42,74,0.12)",
};

// ═══════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════
function Logo({ width = 180, style = {} }) {
  return (
    <img src={LOGO_URL} alt="Patriot Showers" style={{ width, height: "auto", ...style }}
      onError={(e) => {
        e.target.style.display = "none";
        const fb = document.createElement("div");
        fb.innerHTML = `<div style="font-family:Georgia,serif;color:${C.navy};font-size:${width > 160 ? 22 : 16}px;font-weight:700">patriotshowers.com<div style="height:3px;background:${C.red};margin-top:3px;border-radius:2px"></div></div>`;
        e.target.parentElement.appendChild(fb);
      }}
    />
  );
}

function RedAccent() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <div style={{ height: 2.5, background: C.red }} />
      <div style={{ height: 1.5, background: C.red, opacity: 0.5 }} />
    </div>
  );
}

function RepSelector({ onSelect, reps }) {
  const [name, setName] = useState("");
  const [showNew, setShowNew] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (showNew && ref.current) ref.current.focus(); }, [showNew]);

  const sans = "'Segoe UI', system-ui, sans-serif";
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg }}>
      <div style={{ width: "100%", maxWidth: 420, padding: "0 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <Logo width={260} style={{ marginBottom: 20 }} />
          <h2 style={{ fontFamily: sans, color: C.navy, fontSize: 21, fontWeight: 600, margin: "0 0 6px" }}>Sales Assistant</h2>
          <p style={{ fontFamily: sans, color: C.textMuted, fontSize: 14, margin: 0 }}>Product expert · Value engineer · Sales coach</p>
        </div>
        <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: 24, boxShadow: C.shadowLg }}>
          <p style={{ fontFamily: sans, color: C.textMuted, fontSize: 12, fontWeight: 600, margin: "0 0 14px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Select Your Name</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {reps.map((r) => (
              <button key={r} onClick={() => onSelect(r)}
                style={{ padding: "13px 18px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.navy, fontSize: 15, fontWeight: 500, cursor: "pointer", textAlign: "left", transition: "all 0.15s", fontFamily: sans }}
                onMouseEnter={(e) => { e.target.style.borderColor = C.navy; e.target.style.boxShadow = C.shadow; e.target.style.background = C.white; }}
                onMouseLeave={(e) => { e.target.style.borderColor = C.border; e.target.style.boxShadow = "none"; e.target.style.background = C.bg; }}
              >{r}</button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          {!showNew ? (
            <button onClick={() => setShowNew(true)}
              style={{ width: "100%", padding: 13, background: "transparent", border: `1px dashed ${C.borderDark}`, borderRadius: 10, color: C.textMuted, fontSize: 14, cursor: "pointer", fontFamily: sans, transition: "all 0.15s" }}
              onMouseEnter={(e) => { e.target.style.borderColor = C.red; e.target.style.color = C.red; }}
              onMouseLeave={(e) => { e.target.style.borderColor = C.borderDark; e.target.style.color = C.textMuted; }}
            >+ Add New Rep</button>
          ) : (
            <div style={{ display: "flex", gap: 8, background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: 10, boxShadow: C.shadow }}>
              <input ref={ref} value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onSelect(name.trim()); }}
                placeholder="Enter your name"
                style={{ flex: 1, padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.navy, fontSize: 15, outline: "none", fontFamily: sans }} />
              <button onClick={() => name.trim() && onSelect(name.trim())} disabled={!name.trim()}
                style={{ padding: "10px 20px", background: name.trim() ? C.navy : C.card, border: "none", borderRadius: 8, color: C.white, fontSize: 14, fontWeight: 600, cursor: name.trim() ? "pointer" : "default", fontFamily: sans, opacity: name.trim() ? 1 : 0.5 }}>Go</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickActions({ onAction }) {
  const items = [
    { l: "Value engineer this shower", i: "💡", p: "I need help value engineering a shower design to bring the investment down. What are the best strategies?" },
    { l: "Objection: need to think", i: "🎯", p: "The customer just said they need to think about it. What do I say?" },
    { l: "Color options", i: "🎨", p: "What wall colors are available and which are the most popular?" },
    { l: "Seat options", i: "🪑", p: "What shower seat options do we have, from most affordable to premium?" },
    { l: "Standard pan sizes", i: "📐", p: "What are all the standard shower pan sizes available?" },
    { l: "Sales roadmap", i: "🗺️", p: "Walk me through the 9 steps of the sales roadmap so I can figure out where I am." },
    { l: "Manager's Special", i: "⭐", p: "Tell me about the Manager's Special packages and how to present them." },
  ];
  return (
    <div style={{ padding: "12px 0", display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
      {items.map((a, i) => (
        <button key={i} onClick={() => onAction(a.p)}
          style={{ padding: "8px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 20, color: C.navyLight, fontSize: 13, cursor: "pointer", fontFamily: "'Segoe UI', system-ui, sans-serif", fontWeight: 500, transition: "all 0.15s", whiteSpace: "nowrap" }}
          onMouseEnter={(e) => { e.target.style.background = C.redMuted; e.target.style.borderColor = C.red; e.target.style.color = C.red; }}
          onMouseLeave={(e) => { e.target.style.background = C.bg; e.target.style.borderColor = C.border; e.target.style.color = C.navyLight; }}
        >{a.i} {a.l}</button>
      ))}
    </div>
  );
}

function ChatMessage({ msg, isLast }) {
  const isUser = msg.role === "user";
  const fmt = (text) => {
    return text.split(/(\*\*.*?\*\*|".*?")/g).map((p, j) => {
      if (p.startsWith("**") && p.endsWith("**")) return <strong key={j} style={{ color: C.navy, fontWeight: 600 }}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('"') && p.endsWith('"') && !isUser) return <span key={j} style={{ background: C.redMuted, color: C.red, padding: "1px 6px", borderRadius: 4, fontStyle: "italic", fontSize: "0.95em" }}>{p}</span>;
      return p;
    });
  };
  const render = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      if (line.startsWith("**") && line.endsWith("**")) return <p key={i} style={{ fontWeight: 700, margin: "10px 0 4px", color: isUser ? C.white : C.navy, fontSize: 14 }}>{line.replace(/\*\*/g, "")}</p>;
      if (line.startsWith("- ") || line.startsWith("• ")) return <div key={i} style={{ display: "flex", gap: 8, margin: "3px 0", paddingLeft: 4 }}><span style={{ color: C.red, flexShrink: 0, fontWeight: 700 }}>•</span><span style={{ color: isUser ? "rgba(255,255,255,0.95)" : C.textBody }}>{fmt(line.slice(2))}</span></div>;
      if (line.match(/^\d+\.\s/)) { const n = line.match(/^(\d+)\.\s/)[1]; return <div key={i} style={{ display: "flex", gap: 8, margin: "3px 0", paddingLeft: 4 }}><span style={{ color: C.red, fontWeight: 700, flexShrink: 0, minWidth: 20 }}>{n}.</span><span style={{ color: isUser ? "rgba(255,255,255,0.95)" : C.textBody }}>{fmt(line.replace(/^\d+\.\s/, ""))}</span></div>; }
      if (line.trim() === "") return <div key={i} style={{ height: 8 }} />;
      return <p key={i} style={{ margin: "4px 0", lineHeight: 1.65, color: isUser ? "rgba(255,255,255,0.95)" : C.textBody }}>{fmt(line)}</p>;
    });
  };

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", padding: "4px 24px", animation: isLast ? "fadeSlideIn 0.2s ease" : "none" }}>
      {!isUser && <div style={{ width: 30, height: 30, borderRadius: 8, background: C.navy, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10, marginTop: 2, color: C.white, fontSize: 13, fontWeight: 700 }}>★</div>}
      <div style={{
        maxWidth: isUser ? "72%" : "80%", padding: "12px 16px",
        background: isUser ? C.navy : C.white,
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        border: isUser ? "none" : `1px solid ${C.border}`, boxShadow: isUser ? "none" : C.shadow,
        fontSize: 14, fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>
        {render(msg.content)}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", padding: "4px 24px" }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: C.navy, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10, color: C.white, fontSize: 13, fontWeight: 700 }}>★</div>
      <div style={{ padding: "16px 20px", background: C.white, borderRadius: "14px 14px 14px 4px", border: `1px solid ${C.border}`, boxShadow: C.shadow, display: "flex", gap: 5, alignItems: "center" }}>
        {[0, 1, 2].map((i) => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: C.textDim, animation: `bounce 1.2s ease infinite ${i * 0.15}s` }} />)}
      </div>
    </div>
  );
}

function Admin({ conversations, onBack }) {
  const [search, setSearch] = useState("");
  const [selRep, setSelRep] = useState("all");
  const [view, setView] = useState(null);
  const reps = [...new Set(conversations.map((c) => c.rep_name))];
  const filtered = conversations.filter((c) => {
    if (selRep !== "all" && c.rep_name !== selRep) return false;
    if (search) { const s = search.toLowerCase(); return c.rep_name.toLowerCase().includes(s) || (c.messages || []).some((m) => m.content?.toLowerCase().includes(s)); }
    return true;
  });
  const stats = { total: conversations.length, today: conversations.filter((c) => new Date(c.created_at).toDateString() === new Date().toDateString()).length, reps: reps.length };
  const sans = "'Segoe UI', system-ui, sans-serif";

  if (view) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: sans }}>
        <div style={{ padding: "14px 24px", borderBottom: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setView(null)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.navy, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 500 }}>← Back</button>
          <div><div style={{ color: C.navy, fontWeight: 600, fontSize: 15 }}>{view.rep_name}</div><div style={{ color: C.textDim, fontSize: 12 }}>{new Date(view.created_at).toLocaleString()} · {(view.messages || []).length} messages</div></div>
        </div>
        <RedAccent />
        <div style={{ padding: "16px 0", maxWidth: 800, margin: "0 auto" }}>{(view.messages || []).map((m, i) => <ChatMessage key={i} msg={m} isLast={false} />)}</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: sans }}>
      <div style={{ padding: "14px 24px", borderBottom: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Logo width={140} />
          <div style={{ width: 1, height: 28, background: C.border }} />
          <div><h2 style={{ color: C.navy, fontSize: 17, fontWeight: 700, margin: 0 }}>Coaching Dashboard</h2><p style={{ color: C.textDim, fontSize: 12, margin: 0 }}>Review conversations & coach your team</p></div>
        </div>
        <button onClick={onBack} style={{ background: C.navy, border: "none", borderRadius: 8, color: C.white, padding: "9px 18px", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600 }}>← Back to Chat</button>
      </div>
      <RedAccent />
      <div style={{ display: "flex", gap: 12, padding: "20px 24px 0", maxWidth: 900, margin: "0 auto" }}>
        {[{ l: "Total Conversations", v: stats.total, c: C.navy }, { l: "Today", v: stats.today, c: C.red }, { l: "Active Reps", v: stats.reps, c: C.navyMuted }].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "18px 20px", background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
            <div style={{ color: s.c, fontSize: 30, fontWeight: 700, fontFamily: "Georgia, serif" }}>{s.v}</div>
            <div style={{ color: C.textDim, fontSize: 12, fontWeight: 500, marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, padding: "16px 24px", maxWidth: 900, margin: "0 auto" }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations..." style={{ flex: 1, padding: "10px 14px", background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.navy, fontSize: 14, outline: "none", fontFamily: "inherit" }} />
        <select value={selRep} onChange={(e) => setSelRep(e.target.value)} style={{ padding: "10px 14px", background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.navy, fontSize: 14, fontFamily: "inherit", cursor: "pointer" }}>
          <option value="all">All Reps</option>
          {reps.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div style={{ padding: "0 24px 40px", maxWidth: 900, margin: "0 auto" }}>
        {filtered.length === 0 ? <div style={{ textAlign: "center", padding: 60, color: C.textDim, fontSize: 15 }}>{conversations.length === 0 ? "No conversations yet." : "No results."}</div>
          : filtered.map((c, i) => {
            const first = (c.messages || []).find((m) => m.role === "user");
            return (
              <div key={c.id || i} onClick={() => setView(c)} style={{ padding: "16px 20px", background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 8, cursor: "pointer", transition: "all 0.15s", boxShadow: C.shadow }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.boxShadow = C.shadowLg; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = C.shadow; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ background: C.redMuted, color: C.red, padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.rep_name}</span>
                    <span style={{ color: C.textDim, fontSize: 12 }}>{(c.messages || []).length} msgs</span>
                  </div>
                  <span style={{ color: C.textDim, fontSize: 12 }}>{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p style={{ color: C.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{first ? first.content.slice(0, 100) + (first.content.length > 100 ? "..." : "") : "No messages"}</p>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════
export default function App() {
  const [rep, setRep] = useState(null);
  const [reps, setReps] = useState(["Sam", "Rep 1", "Rep 2"]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [convos, setConvos] = useState([]);
  const [convoId, setConvoId] = useState(null);
  const [sidebar, setSidebar] = useState(false);
  const [history, setHistory] = useState([]);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const dbOk = SUPABASE_URL !== "YOUR_SUPABASE_URL";
  const apiOk = ANTHROPIC_API_KEY !== "YOUR_ANTHROPIC_API_KEY";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const loadConvos = useCallback(async () => {
    if (!dbOk) return;
    try { const d = await supabaseQuery("conversations", "GET", null, { select: "*", order: "created_at.desc" }); if (Array.isArray(d)) setConvos(d); } catch (e) {}
  }, [dbOk]);

  const loadHistory = useCallback(async (name) => {
    if (!dbOk) return;
    try { const d = await supabaseQuery("conversations", "GET", null, { select: "*", rep_name: `eq.${name}`, order: "created_at.desc", limit: "20" }); if (Array.isArray(d)) setHistory(d); } catch (e) {}
  }, [dbOk]);

  const pickRep = (name) => { setRep(name); if (!reps.includes(name)) setReps([...reps, name]); setMsgs([]); setConvoId(null); loadHistory(name); };

  const save = async (m) => {
    if (!dbOk) return;
    try {
      if (convoId) { await supabaseQuery("conversations", "PATCH", { messages: m }, { id: `eq.${convoId}` }); }
      else { const d = await supabaseQuery("conversations", "POST", { rep_name: rep, messages: m, created_at: new Date().toISOString() }); if (d?.[0]?.id) setConvoId(d[0].id); }
    } catch (e) {}
  };

  const send = async (content) => {
    if (!content.trim() || loading) return;
    const userMsg = { role: "user", content: content.trim() };
    const next = [...msgs, userMsg];
    setMsgs(next); setInput(""); setLoading(true);

    if (!apiOk) {
      setMsgs([...next, { role: "assistant", content: "**API key not configured.** Add your Anthropic API key:\n\n1. Create a .env file in your project root\n2. Add: REACT_APP_ANTHROPIC_API_KEY=sk-ant-your-key-here\n3. Restart the dev server (npm start)\n\nGet your key at console.anthropic.com" }]);
      setLoading(false); return;
    }

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": ANTHROPIC_API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1500,
          system: SYSTEM_PROMPT,
          messages: next.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error?.message || `API returned ${res.status}`);
      }
      
      const data = await res.json();
      const ai = data.content?.map((c) => c.text || "").join("\n") || "Could not generate a response.";
      const final = [...next, { role: "assistant", content: ai }];
      setMsgs(final); save(final);
    } catch (e) {
      setMsgs([...next, { role: "assistant", content: `**Connection error:** ${e.message}\n\nCheck your API key and internet connection.` }]);
    }
    setLoading(false); inputRef.current?.focus();
  };

  const newChat = () => { if (msgs.length > 0) loadHistory(rep); setMsgs([]); setConvoId(null); inputRef.current?.focus(); };

  if (admin) return <Admin conversations={convos} onBack={() => setAdmin(false)} />;
  if (!rep) return <RepSelector onSelect={pickRep} reps={reps} />;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: C.bg, fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
      <style>{`
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce { 0%,60%,100% { transform:translateY(0); } 30% { transform:translateY(-4px); } }
        * { box-sizing:border-box; }
        input::placeholder { color:${C.textDim}; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }
      `}</style>

      {/* Sidebar overlay */}
      {sidebar && <div onClick={() => setSidebar(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,42,74,0.15)", zIndex: 40 }} />}

      {/* Sidebar */}
      <div style={{ position: "fixed", left: sidebar ? 0 : -300, top: 0, bottom: 0, width: 300, background: C.white, borderRight: `1px solid ${C.border}`, zIndex: 50, transition: "left 0.25s ease", display: "flex", flexDirection: "column", boxShadow: sidebar ? C.shadowXl : "none" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: C.navy, fontWeight: 600, fontSize: 15 }}>Chat History</span>
          <button onClick={() => setSidebar(false)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18, padding: "4px 8px" }}>✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
          {history.length === 0 ? <p style={{ color: C.textDim, fontSize: 13, textAlign: "center", padding: 20 }}>{dbOk ? "No past conversations." : "Connect Supabase to save history."}</p>
            : history.map((c, i) => {
              const first = (c.messages || []).find((m) => m.role === "user");
              return (
                <div key={c.id || i} onClick={() => { setMsgs(c.messages || []); setConvoId(c.id); setSidebar(false); }}
                  style={{ padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = C.navy}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = C.border}>
                  <p style={{ color: C.navy, fontSize: 13, margin: "0 0 4px", fontWeight: 500 }}>{first ? first.content.slice(0, 55) + "..." : "Empty"}</p>
                  <span style={{ color: C.textDim, fontSize: 11 }}>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              );
            })}
        </div>
      </div>

      {/* HEADER */}
      <div style={{ padding: "10px 20px", borderBottom: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setSidebar(true)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 20, padding: "4px 8px", lineHeight: 1 }}>☰</button>
          <Logo width={140} />
          <div style={{ width: 1, height: 24, background: C.border, margin: "0 4px" }} />
          <div>
            <span style={{ color: C.navy, fontSize: 14, fontWeight: 600 }}>Sales Assistant</span>
            <span style={{ color: C.textDim, fontSize: 12, marginLeft: 8 }}>Hey {rep}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={newChat} style={{ padding: "7px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, transition: "all 0.15s" }}
            onMouseEnter={(e) => { e.target.style.borderColor = C.navy; e.target.style.color = C.navy; }}
            onMouseLeave={(e) => { e.target.style.borderColor = C.border; e.target.style.color = C.textMuted; }}>+ New Chat</button>
          <button onClick={() => { loadConvos(); setAdmin(true); }} style={{ padding: "7px 14px", background: C.redMuted, border: `1px solid rgba(200,35,44,0.15)`, borderRadius: 8, color: C.red, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>📊 Coach</button>
          <button onClick={() => { setRep(null); setMsgs([]); setConvoId(null); }} style={{ padding: "7px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Switch</button>
        </div>
      </div>
      <RedAccent />

      {/* MESSAGES */}
      <div style={{ flex: 1, overflow: "auto", paddingTop: 16, paddingBottom: 12 }}>
        {msgs.length === 0 && (
          <div style={{ padding: "48px 24px 24px", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, margin: "0 auto 16px", background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: 24, fontWeight: 700 }}>★</div>
            <h2 style={{ color: C.navy, fontSize: 22, fontWeight: 700, margin: "0 0 8px", fontFamily: "Georgia, serif" }}>How can I help, {rep}?</h2>
            <p style={{ color: C.textMuted, fontSize: 14, margin: "0 0 24px", maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>Product questions, value engineering, design ideas, or sales coaching.</p>
            <QuickActions onAction={send} />
          </div>
        )}
        {msgs.map((m, i) => <ChatMessage key={i} msg={m} isLast={i === msgs.length - 1} />)}
        {loading && <Typing />}
        <div ref={endRef} />
      </div>

      {/* INPUT */}
      <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${C.border}`, background: C.white, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, maxWidth: 800, margin: "0 auto" }}>
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Ask about products, value engineering, or sales coaching..."
            style={{ flex: 1, padding: "12px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.navy, fontSize: 14, outline: "none", fontFamily: "inherit", transition: "border-color 0.15s" }}
            onFocus={(e) => e.target.style.borderColor = C.navy}
            onBlur={(e) => e.target.style.borderColor = C.border} />
          <button onClick={() => send(input)} disabled={!input.trim() || loading}
            style={{ padding: "12px 20px", background: input.trim() && !loading ? C.red : C.card, border: "none", borderRadius: 10, color: C.white, fontSize: 15, fontWeight: 700, cursor: input.trim() && !loading ? "pointer" : "default", fontFamily: "inherit", transition: "all 0.15s", opacity: input.trim() && !loading ? 1 : 0.5, minWidth: 52 }}>
            {loading ? "···" : "→"}
          </button>
        </div>
        <p style={{ textAlign: "center", color: C.textDim, fontSize: 11, margin: "8px 0 0" }}>Internal rep tool · Never shows costs · Verify specs in the design app</p>
      </div>
    </div>
  );
}
